import React, { useEffect, useState } from 'react'
import 'project/polyfill'
import Link from 'next/link'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { sdk } from 'common/sdk'
import Strings from 'project/localisation'
import usePreview from 'project/usePreview'
import useWindowSize from 'components/util/useWindowSize'
import OffersByTag from 'components/marketplace/OffersByTag'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import PageContainer from 'components/util/containers/PageContainer'
import StaffPicksOffers from 'components/marketplace/StaffPicksOffers'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { marketplaceStrings } from 'common/strings/marketplace-strings'
import { OfferSummary, PagedResponse, Tag } from 'common/types/responses'

type GroupedOffer = {
  tagId: string | null
  offers: OfferSummary[]
}

export type MarketplacePage = {
  _tags: Tag[]
  _offerGroup: GroupedOffer[]
  _featuredOffers?: PagedResponse<OfferSummary>
}
const MarketplacePage: NextPageWithLayout<MarketplacePage> = ({
  _featuredOffers,
  _offerGroup,
  _tags,
}) => {
  const size = useWindowSize()
  const [featuredOffers, setFeaturedOffers] = useState(_featuredOffers)
  const [offerGroup, setOfferGroup] = useState(_offerGroup)
  const [tags, setTags] = useState(_tags)
  const { preview } = usePreview()
  useEffect(() => {
    if (preview) {
      getData().then(({ featuredOffers, offerGroup, tags }) => {
        setFeaturedOffers(featuredOffers)
        setTags(tags)
        setOfferGroup(offerGroup)
      })
    }
  }, [preview])
  const seoProps: SeoContainerProps['seoProps'] = {
    description: marketplaceStrings.seoDescription,
    openGraph: {
      description: marketplaceStrings.seoDescription,
      images: [
        {
          alt: 'Hoxton Mix',
          height: 226,
          url: `${process.env.SITE_URL}/_next/image?url=${encodeURIComponent(
            require('../../public/images/homepage-hoxton-mix-team-desktop.png')
              .default.src,
          )}&w=400&q=75`,
          width: 400,
        },
      ],
      site_name: 'Hoxton Mix',
      title: marketplaceStrings.seoTitle,
      type: 'website',
      url: 'https://www.hoxtonmix.com/marketplace/',
    },
    title: marketplaceStrings.seoTitle,
    twitter: {
      cardType: 'summary_large_image',
    },
  }

  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/marketplace/',
      name: `${Strings.marketPlace}`,
      position: 2,
    },
  ])
  const allFeaturedOffers = featuredOffers?.results

  const handleFilter = (tagId: string) => {
    const tagSection = document.getElementById(tagId)
    if (tagSection) {
      tagSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (!tags?.length || !offerGroup?.length) {
    throw new Error(
      `Unexpected offers response from API. Tags: ${tags}. Offers: ${offerGroup}`,
    )
  }

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <div className='new-design'>
        <PartnerHeroBanner
          robotImage={{
            height: 635,
            src: '/images/new/robot-pointing-left-v2.png',
            width: 787,
          }}
          robotImageMobile={{
            height: 506,
            src: '/images/new/robot-holding-mail.png',
            width: 392,
          }}
          title={
            <h1 className='fs-fluid-36-64 fw-bold mb-lg-3 mb-2 lh-114 text-center text-lg-start'>
              <span className='text-pink6'>Marketplace:</span> Business is
              better with the right apps & services
            </h1>
          }
          description={
            <div className='fs-fluid-24-38 fw-semibold text-pink6 text-center text-lg-start'>
              The Hoxton Mix works with a variety of amazing partners who offer
              products and services that could boost your business.
            </div>
          }
        />
        <div className='container marketplace mb-8 mb-md-22 px-lg-0'>
          {!!tags?.length && (
            <>
              <h4>{marketplaceStrings.popularCategories}</h4>
              <div className='category-container mt-4 gap-2'>
                {tags?.map((tag) => (
                  <button
                    className='chip'
                    key={tag.id}
                    onClick={() => handleFilter(tag.id)}
                  >
                    <span className='text-xs text-semibold'>{tag.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {!!allFeaturedOffers?.length && (
            <StaffPicksOffers offers={allFeaturedOffers} />
          )}
          {tags &&
            offerGroup?.map((offer) => {
              const tag = tags?.find((tag) => tag.id === offer.tagId) as Tag
              if (tag) {
                return (
                  <OffersByTag offers={offer.offers} tag={tag} key={tag.id} />
                )
              }
            })}
        </div>
        <div className='bg-black1 text-center py-6 text-white'>
          <h4 className='fs-fluid-24-32 mb-0'>
            <span className={'text-semibold'}>
              {marketplaceStrings.wantAFeatureOrService}
            </span>
            <Link className='fw-normal ms-6' href={'/marketplace-partner'}>
              {marketplaceStrings.becomeAPartner}
            </Link>
          </h4>
        </div>
        <div className='spacer'></div>
      </div>
    </SeoContainer>
  )
}

export default MarketplacePage

MarketplacePage.getLayout = (page) => (
  <PageContainer hideFooterCard>{page}</PageContainer>
)

const getData = async function () {
  const tags = await sdk.getTags({ types: ['OFFER'] })

  const offers = await Promise.all(
    tags.map((tag) => sdk.getAllOffers({ tags: [tag.id] })),
  )

  const featuredOffers = await sdk.getAllOffers({
    featured: true,
  })
  const offerGroup = offers.map((offer) => {
    const data = {
      offers: offer.results,
      tagId: offer.results[0]?.tags[0] || null,
    }
    return data
  })

  return { featuredOffers, offerGroup, tags }
}
export async function getStaticProps() {
  try {
    const { featuredOffers, offerGroup, tags } = await getData()
    return {
      props: {
        _featuredOffers: featuredOffers,
        _offerGroup: offerGroup,
        _tags: tags.filter((v) => offerGroup.find((g) => g.tagId === v.id)),
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}
